import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogBody, DialogTitle } from './dialog';
import { Button } from './button';
import { Text } from './text';
import {
  DocumentIcon,
  PhotoIcon,
  DocumentTextIcon,
  FolderIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';
import { collection, query, orderBy, limit, startAfter, getDocs } from 'firebase/firestore';
import { db } from '../firebase';

const BATCH_SIZE = 30;

// Helper to format bytes
const formatBytes = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

// Helper to format date
const formatDate = (timestamp) => {
  if (!timestamp) return '-';
  const date = typeof timestamp === 'number' ? new Date(timestamp) : timestamp.toDate?.() || new Date(timestamp);
  return new Intl.DateTimeFormat('es-MX', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
  }).format(date);
};

// Get icon based on file type/extension
const getFileIcon = (fileName, contentType) => {
  const ext = fileName?.split('.').pop()?.toLowerCase();

  if (contentType?.startsWith('image/') || ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(ext)) {
    return <PhotoIcon className="w-5 h-5 text-purple-500" />;
  }
  if (contentType === 'application/pdf' || ext === 'pdf') {
    return <DocumentTextIcon className="w-5 h-5 text-red-500" />;
  }
  if (['xml'].includes(ext)) {
    return <DocumentTextIcon className="w-5 h-5 text-orange-500" />;
  }
  if (['doc', 'docx'].includes(ext)) {
    return <DocumentTextIcon className="w-5 h-5 text-blue-500" />;
  }
  if (['xls', 'xlsx'].includes(ext)) {
    return <DocumentTextIcon className="w-5 h-5 text-green-500" />;
  }
  return <DocumentIcon className="w-5 h-5 text-gray-500" />;
};

// Extract readable name from storage path
const getDisplayName = (filePath) => {
  if (!filePath) return 'Archivo sin nombre';
  const parts = filePath.split('/');
  return parts[parts.length - 1] || filePath;
};

const StorageFilesModal = ({ isOpen, onClose, orgId }) => {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [lastDoc, setLastDoc] = useState(null);
  const [hasMore, setHasMore] = useState(true);

  // Fetch initial batch of files
  const fetchFiles = useCallback(async () => {
    if (!orgId) return;

    setLoading(true);
    setError(null);
    try {
      const filesRef = collection(db, 'orgs', orgId, 'storage_usage', 'current', 'stored_files');
      const q = query(filesRef, orderBy('uploaded_at', 'desc'), limit(BATCH_SIZE));

      const snapshot = await getDocs(q);

      const filesList = [];
      snapshot.forEach((doc) => {
        filesList.push({ id: doc.id, ...doc.data() });
      });

      setFiles(filesList);
      setLastDoc(snapshot.docs[snapshot.docs.length - 1] || null);
      setHasMore(snapshot.docs.length === BATCH_SIZE);
    } catch (err) {
      console.error('Error fetching files:', err);
      setError('Error al cargar los archivos: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  // Load more files
  const loadMore = async () => {
    if (!orgId || !lastDoc || loadingMore) return;

    setLoadingMore(true);
    try {
      const filesRef = collection(db, 'orgs', orgId, 'storage_usage', 'current', 'stored_files');
      const q = query(
        filesRef,
        orderBy('uploaded_at', 'desc'),
        startAfter(lastDoc),
        limit(BATCH_SIZE)
      );

      const snapshot = await getDocs(q);
      const newFiles = [];
      snapshot.forEach((doc) => {
        newFiles.push({ id: doc.id, ...doc.data() });
      });

      setFiles((prev) => [...prev, ...newFiles]);
      setLastDoc(snapshot.docs[snapshot.docs.length - 1] || null);
      setHasMore(snapshot.docs.length === BATCH_SIZE);
    } catch (err) {
      console.error('Error loading more files:', err);
    } finally {
      setLoadingMore(false);
    }
  };

  // Fetch files when modal opens
  useEffect(() => {
    if (isOpen && orgId) {
      setFiles([]);
      setLastDoc(null);
      setHasMore(true);
      fetchFiles();
    }
  }, [isOpen, orgId, fetchFiles]);

  // Reset state when closing
  const handleClose = () => {
    setFiles([]);
    setLastDoc(null);
    setHasMore(true);
    setError(null);
    onClose();
  };

  return (
    <Dialog open={isOpen} onClose={handleClose} size="2xl">
      <DialogTitle>
        <div className="flex items-center gap-2">
          <FolderIcon className="w-5 h-5 text-azul-surelia" />
          Archivos Almacenados
        </div>
      </DialogTitle>
      <DialogBody>
        {/* Error State */}
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <Text className="text-sm text-red-700">{error}</Text>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="flex items-center justify-center py-12">
            <ArrowPathIcon className="w-8 h-8 text-azul-surelia animate-spin" />
          </div>
        )}

        {/* Empty State */}
        {!loading && files.length === 0 && !error && (
          <div className="text-center py-12">
            <FolderIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <Text className="text-gray-500">No hay archivos almacenados</Text>
          </div>
        )}

        {/* Files List */}
        {!loading && files.length > 0 && (
          <div className="space-y-2 max-h-[60vh] overflow-y-auto">
            {/* Header */}
            <div className="grid grid-cols-12 gap-2 px-3 py-2 bg-gray-100 rounded-lg text-xs font-medium text-gray-600 sticky top-0">
              <div className="col-span-5">Archivo / Cliente</div>
              <div className="col-span-3">Tipo</div>
              <div className="col-span-2 text-right">Tamaño</div>
              <div className="col-span-2 text-right">Fecha</div>
            </div>

            {/* File Rows */}
            {files.map((file) => (
              <div
                key={file.id}
                className="grid grid-cols-12 gap-2 px-3 py-2 bg-white border border-gray-100 rounded-lg hover:bg-gray-50 transition-colors items-center"
              >
                <div className="col-span-5 flex items-center gap-2 min-w-0">
                  {getFileIcon(file.file_name, file.file_type)}
                  <div className="min-w-0">
                    <span className="text-sm text-gray-800 truncate block" title={file.file_name}>
                      {file.file_name || getDisplayName(file.path)}
                    </span>
                    <span className="text-xs text-gray-400 truncate block" title={file.client_name}>
                      {file.client_name}
                    </span>
                  </div>
                </div>
                <div className="col-span-3">
                  <span className="text-xs text-azul-surelia/70 bg-azul-surelia/10 px-2 py-0.5 rounded">
                    {file.file_type || 'archivo'}
                  </span>
                </div>
                <div className="col-span-2 text-right">
                  <span className="text-sm text-gray-600">
                    {formatBytes(file.size_bytes || 0)}
                  </span>
                </div>
                <div className="col-span-2 text-right">
                  <span className="text-xs text-gray-500">
                    {formatDate(file.uploaded_at)}
                  </span>
                </div>
              </div>
            ))}

            {/* Load More Button */}
            {hasMore && (
              <div className="pt-4 text-center">
                <Button
                  color="white"
                  onClick={loadMore}
                  disabled={loadingMore}
                  className="w-full"
                >
                  {loadingMore ? (
                    <span className="flex items-center justify-center gap-2">
                      <ArrowPathIcon className="w-4 h-4 animate-spin" />
                      Cargando...
                    </span>
                  ) : (
                    'Cargar mas archivos'
                  )}
                </Button>
              </div>
            )}

            {/* End of list indicator */}
            {!hasMore && files.length > 0 && (
              <div className="pt-4 text-center">
                <Text className="text-xs text-gray-400">
                  Mostrando {files.length} archivo{files.length !== 1 ? 's' : ''}
                </Text>
              </div>
            )}
          </div>
        )}

        {/* Footer */}
        <div className="mt-6 pt-4 border-t border-gray-200 flex justify-end">
          <Button color="white" onClick={handleClose}>
            Cerrar
          </Button>
        </div>
      </DialogBody>
    </Dialog>
  );
};

export default StorageFilesModal;
